import type {
  HasFetchedRecord,
  HasFetchedState,
  HasFetchedValue,
} from "shared/types";

const REFETCH_TIME = parseInt(
  import.meta.env.VITE_REFETCH_DATA_TIME || "30000",
);

export const shouldFetch = (
  hasFetched: HasFetchedValue | HasFetchedRecord,
): boolean => {
  if (hasFetched === false || hasFetched === undefined) {
    return true;
  }

  if (hasFetched instanceof Date) {
    const currentTime = new Date().getTime();
    const fetchTime = hasFetched.getTime();
    const elapsedTime = currentTime - fetchTime;

    return elapsedTime >= REFETCH_TIME;
  }

  return false;
};

type ShouldSkipPaginatedFetchOptions = {
  hasFetched: HasFetchedValue | HasFetchedRecord;
  forceFetch?: boolean;
  page?: number;
  perPage?: number;
};

/**
 * Determines if a paginated fetch should be skipped.
 * Returns true if the fetch should be skipped (i.e., use cached data).
 * Always returns false (don't skip) when pagination params are provided.
 */
export const shouldSkipPaginatedFetch = ({
  hasFetched,
  forceFetch = false,
  page,
  perPage,
}: ShouldSkipPaginatedFetchOptions): boolean => {
  const hasPaginationParams = page !== undefined || perPage !== undefined;

  if (hasPaginationParams) {
    return false;
  }

  if (forceFetch) {
    return false;
  }

  return shouldFetch(hasFetched) === false;
};

/**
 * Runs `fetcher` and caches the in-flight promise in `cache[key]` so
 * concurrent callers await the same request instead of firing duplicates.
 * Callers keep full control of their own `HasFetchedState`/`shouldFetch`
 * staleness check and any `forceFetch`/`isLoading` handling around the call
 * — this only extracts the repeated "isFetching ref + try/finally" plumbing.
 *
 * @example
 * ```ts
 * const loadFilters = (teacherId: number) =>
 *   fetchOnce(isFetching.value, "myStudentsFilters", async () => {
 *     const result = await fetchMyStudentsMeetingsFilters(teacherId);
 *     myStudentsFilters.value = result;
 *     hasFetched.value.myStudentsFilters = new Date();
 *     return result;
 *   });
 * ```
 */
export const fetchOnce = <T>(
  cache: Record<string | number, unknown>,
  key: string | number,
  fetcher: () => Promise<T>,
): Promise<T> => {
  const inFlight = cache[key];
  if (inFlight) {
    return inFlight as Promise<T>;
  }

  const promise = (async () => {
    try {
      return await fetcher();
    } finally {
      cache[key] = false;
    }
  })();

  cache[key] = promise;
  return promise;
};

/**
 * Resets all keys in a HasFetchedState object to their initial state.
 * - Simple values (Date | false) are reset to `false`
 * - Nested records (HasFetchedRecord) are reset to empty objects `{}`
 *
 * @example
 * ```ts
 * const hasFetched = ref<HasFetchedState>({
 *   tasks: false,
 *   tasksById: {},
 *   filters: false,
 * });
 *
 * // In $reset():
 * resetHasFetched(hasFetched.value);
 * // Result: { tasks: false, tasksById: {}, filters: false }
 * ```
 */
export const resetHasFetched = (state: HasFetchedState): void => {
  Object.keys(state).forEach((key) => {
    const value = state[key];

    if (value instanceof Date || value === false) {
      state[key] = false;
    } else if (typeof value === "object" && value !== null) {
      state[key] = {};
    }
  });
};
